import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Typography, useMediaQuery } from "@mui/material"
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RiCloseFill } from 'react-icons/ri'
import { HiOutlineSearch } from "react-icons/hi";

import MainContainer from "../../components/Container/MainContainer"
import AppContentHeader from "../../components/AppContentHeader";
import BlockContainer from "../../components/Container/BlockContainer";
// import BaseImage from "../../components/BaseImage"
import BaseIconInput from "../../components/BaseIconInput";
import NutritionFilterMobileNav from "../../components/NutritionFilterMobileNav";
import NutritionFilterDropdown from "../../components/NutritionFilterDropdown";
import NutritionAccordion from "../../components/NutritionAccordion";
import { closeModal, setNutritionFilterOptions } from "../../store";
import { removeArrayItem } from "../../hooks/use-remove-array-item";

const NutritionCategory = () => {
  const sm = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const xs = useMediaQuery((theme) => theme.breakpoints.down('ssm'));
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { type, filterKey } = useParams()
  const reports = useSelector(state => state.patientReports)
  const nutritionData = useMemo(() => reports.find(item => item.name === 'Nutrition'), [reports])
  const filterDropdownLabel = useMemo(() => type === 'guidance' ? 'Category' : 'Guidance Level', [type])

  useMemo(() => {
    if (xs) {
      dispatch(closeModal())
    }
  }, [xs, dispatch])

  const filterOptions = useSelector(state => state.nutritionFilterOptions)

  const [searchKey, setSearchKey] = useState('')
  const [filters, setFilters] = useState([])
  const [openFilterNav, setOpenFilterNav] = useState(false)
  const [filteredData, setFilteredData] = useState([])
  
  const sumscores = useMemo(() => {
    const sumscoresData = nutritionData?.data?.content?.nutrition?.sumscores;
    if (sumscoresData) {
      const sumscoresArray = Object.keys(sumscoresData).map(key => {
        const item = sumscoresData[key]
        let guidanceLevel
        let percent
        if (item.rating === 1) {
          guidanceLevel = 'Low'
          percent = 25
        }
        if (item.rating === 2) {
          guidanceLevel = 'Moderate'
          percent = 48
        }
        if (item.rating === 3) {
          guidanceLevel = 'High'
          percent = 95
        }

        const genoType = Object.keys(item.rsids).map(key => {
          const value = item['rsids'][key]['genotype']?.toString().replace(',', '/') || ''
          return `${key} ${value}`
        })
        
        return {
          guidanceLevel,
          percent,
          genoType,
          ...item
        }
      })
      return sumscoresArray
    } else return []
  }, [nutritionData])

  const categories = useMemo(() => {
    if (sumscores && sumscores.length > 0) {
      const categoryObj = sumscores.reduce((catsSoFar, current) => {
        let groupKey = current['category']
        if (!catsSoFar[groupKey]) catsSoFar[groupKey] = [];
        catsSoFar[groupKey].push(current);
        return catsSoFar;
      }, {});
      
      return Object.keys(categoryObj).sort((a, b) => a.localeCompare(b))
    } 
    return []
  }, [sumscores])

  useMemo(() => {
    let options
    if (type === 'category') {
      options = {
        category: [filterKey]
      }
    } else {
      options = {
        guidanceLevels: [filterKey]
      }
    }
    dispatch(setNutritionFilterOptions(options))
  }, [dispatch, type, filterKey])

  useMemo(() => {
    let nutritionTemp = [...sumscores]
    
    if (filterOptions?.guidanceLevels && filterOptions?.guidanceLevels.length > 0) {
      const filtersTemp = []
      nutritionTemp.forEach(item => {
        filterOptions?.guidanceLevels.forEach(level => {
          if (item.guidanceLevel.toLowerCase() === level.toLowerCase()) {
            filtersTemp.push(item)
          }
        })
      })
      nutritionTemp = [...filtersTemp]
    }
  
    if (filterOptions?.searchKey) {
      nutritionTemp = nutritionTemp.filter(item => item.name.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        // item.brandNames?.toString().toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.category.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.condition?.toString().toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        // item.descriptionTitle.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()) || 
        item.guidanceLevel.toLowerCase().includes(filterOptions?.searchKey.toLowerCase()))
    }
    if (filterOptions?.category && filterOptions?.category.length > 0) {
      const filtersTemp = []
      nutritionTemp.forEach(item => {
        filterOptions?.category?.forEach(option => {
          if (item.category.toLowerCase() === option.toLowerCase()) {
            filtersTemp.push(item)
          }
        })
      })
      nutritionTemp = [...filtersTemp]
    }
    setFilteredData(nutritionTemp.sort((a, b) => a.name.localeCompare(b.name)))
  }, [filterOptions, sumscores])

  useMemo(() => {
    if (filterOptions) {
      const temp = []
      if (filterOptions?.guidanceLevels && filterOptions?.guidanceLevels.length > 0) {
        temp.push({
          type: 'guidance level',
          label: `Guidance level: ${filterOptions?.guidanceLevels?.toString()}`,
        })
      }
      if (filterOptions?.searchKey) temp.push({
        type: 'search',
        label: `Filter pill #${filterOptions.searchKey}`,
        value: filterOptions.searchKey
      })
      if (filterOptions?.category) filterOptions?.category.map((val => temp.push({
        type: 'category',
        label: `Filter pill #${val}`,
        value: val
      })))
      setFilters(temp)
    }
  }, [filterOptions])

  const filterLength = useMemo(() => {
    if (xs) {
      return filters.length - 1
    } else if (sm) {
      return filters.length - 2
    }
  }, [sm, xs, filters])

  const handleFilter = () => {
    const options = {
      ...filterOptions,
      searchKey: searchKey
    }
    dispatch(setNutritionFilterOptions(options))
  }

  const handleClickSearch = () => {
    if (filterOptions.searchKey) {
      handleRemoveFilter(filterOptions?.searchKey)
    }
    handleFilter(searchKey)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      // 👇 Get input value
      handleClickSearch()
    }
  };

  const handleRemoveFilter = (filter) => {
    let options = filterOptions
    if (filter.type === 'guidance level') {
      options = {
        ...options,
        guidanceLevels: []
      }
    } else if (filter.type === 'search') {
      setSearchKey('')
      options = {
        ...options,
        searchKey: ''
      }
    } else if (filter.type === 'category') {
      const categories = options.category
      options = {
        ...options,
        category: removeArrayItem(categories, filter.value)
      }
    }
    dispatch(setNutritionFilterOptions(options))
  }

  const handleClickSort = () => {
    dispatch(setNutritionFilterOptions({
      ...filterOptions,
      guidanceLevelSortDir: filterOptions.guidanceLevelSortDir === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <MainContainer
      sx={{
        padding: 0,
        borderRadius: 0,
        overflowX: 'hidden'
      }}
    >
      <Box
        sx={{
          position: 'relative'
        }}
      >
        <Box className='content-header-wave-white'/>
        <Box
          sx={{
            position: 'relative',
            padding: '16px 20px',
            zIndex: 10,
            mt: '-90px'
          }}
        >
          <BlockContainer>
            <AppContentHeader 
              backLabel=''
              hasBackLink
              onBack={() => navigate(-1)}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: '120px',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  '& h1::first-letter': {
                    textTransform: 'uppercase'
                  }
                }}
              >
                <Typography 
                  variant="h1"
                >
                  {type === 'guidance' ? `${filterKey} risk genetic traits` : filterKey}
                </Typography>
              </Box>
            </Box>
          </BlockContainer>
        </Box>
      </Box>
      <Box
        sx={{
          padding: { xs: '0', md: '40px 20px' },
          position: 'relative',
        }}
      >
        <BlockContainer>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '8px',
              padding: '20px 16px',
              zIndex: 10,
              position: 'relative',
              boxShadow: '0px 2px 10px 0px #5B87EF33'
            }}
          >
            {openFilterNav && (
              <NutritionFilterMobileNav
                isOpen={openFilterNav}
                setIsOpen={setOpenFilterNav}
                type={type}
                categories={categories}
              />
            )}
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%'
                  }}
                >
                  <Box>
                    <Typography variant="h2">
                      Genetic traits ({filteredData.length})
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: { xs: '100%', md: 'auto' },
                      mt: { xs: '12px', md: 0 }
                    }}
                  >
                    <BaseIconInput
                      height='48'
                      placeholder='Search'
                      inputStyles={{
                        background: '#4266B833',
                        borderRadius: '40px',
                        color: '#46545F',
                        border: '#4266b800'
                      }}
                      sx={{
                        maxWidth: { xs: '100%', md: '400px' }
                      }}
                      startIcon={<HiOutlineSearch size={20} />}
                      endIcon={<RiCloseFill size={24} />}
                      value={searchKey}
                      onChange={(e) => setSearchKey(e.target.value)}
                      onClickStartIcon={handleClickSearch}
                      onClickEndIcon={() => {
                        let count = 0;
                        while(count < filters.length) {
                          if(filters[count]) {
                            handleRemoveFilter(filters[count])
                          }
                          count ++;
                        }
                        setSearchKey('')
                      }}
                      onKeyDown={handleKeyDown}
                      fullWidth
                    />
                    <NutritionFilterDropdown
                      setOpenFilterNav={setOpenFilterNav}
                      label={filterDropdownLabel}
                      type={type}
                      categories={categories}
                    />
                  </Box>
                </Box>
              </Box>
              <Box>
                <Stack
                  direction={'row'}
                  flexWrap={'wrap'}
                  sx={{
                    display: 'none'
                  }}
                >
                  {filters.map(((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: { xs: idx === 0 ? 'flex' : 'none', ssm: idx > 1 ? 'none' : 'flex', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '2px 6px 2px 6px',
                        height: '24px',
                        borderRadius: '8px',
                        bgcolor: 'primary.lightBlue',
                        mr: '8px',
                        mt: '8px'
                      }}
                    >
                      <Typography variant="body"
                        sx={{
                          mr: '8px',
                          width: { xs: 'auto', ssm: '100px', md: 'auto' },
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}>
                        {item.label}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleRemoveFilter(item)}
                      >
                        <RiCloseFill size={20} />
                      </Box>
                    </Box>
                  )))}
                  {filterLength > 0 && (
                    <Box
                      sx={{
                        display: { xs: 'flex', sm: 'none' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '2px 6px',
                        height: '24px',
                        minHeight: '24px',
                        borderRadius: '8px',
                        bgcolor: 'primary.lightBlue',
                        mr: '8px',
                        mt: '8px'
                      }}
                    >
                      <Typography variant="body" mr={'8px'}>
                        {`+${filterLength}`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                <Box
                  sx={{
                    backgroundColor: 'primary.black',
                    boxShadow: '0px 2px 10px 0px #5B87EF33',
                    height: '64px',
                    display: 'none',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: '4px',
                    padding: '16px',
                    mt: '8px',
                    mb: "16px"
                  }}
                >
                  <Typography variant="h3" color="white">
                    Drug name
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={handleClickSort}
                  >
                    <Typography variant="body" color="white">
                      Guidance Level
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        ml: '4px'
                      }}
                    >
                      <ExpandLessIcon
                        sx={{
                          color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'asc' ? 'white' : 'primary.mainBlue' : 'white',
                          fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'asc' ? '14px' : '15px'
                        }}
                      />
                      <ExpandMoreIcon
                        sx={{
                          color: filterOptions?.guidanceLevelSortDir ? filterOptions?.guidanceLevelSortDir === 'desc' ? 'white' : 'primary.mainBlue' : 'white',
                          fontSize: filterOptions?.guidanceLevelSortDir && filterOptions?.guidanceLevelSortDir === 'desc' ? '14px' : '15px'
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Grid container spacing={'8px'} mt='1px'>
                  {filteredData && filteredData.map(((item, idx) => (
                    <Grid item xs={12}  key={`${item.name}-${idx}`}>
                      <NutritionAccordion item={item} />
                    </Grid>
                  )))}
                </Grid>
              </Box>
            </Box>
          </Box>
        </BlockContainer>
      </Box>
    </MainContainer>
  )
}

export default NutritionCategory